import React, { createContext, useState, useContext, useEffect } from 'react';

const CurrencyContext = createContext();

export const useCurrency = () => {
  const context = useContext(CurrencyContext);
  if (!context) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return context;
};

export const CurrencyProvider = ({ children }) => {
  // Default currency should be PKR
  const [currency, setCurrency] = useState(() => {
    const saved = localStorage.getItem('currency');
    return saved || 'PKR'; // Changed default to PKR
  });

  const [exchangeRate, setExchangeRate] = useState(() => {
    const saved = localStorage.getItem('exchangeRate');
    return saved ? parseFloat(saved) : 370.08;
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Save to localStorage whenever currency or exchangeRate changes
  useEffect(() => {
    localStorage.setItem('currency', currency);
  }, [currency]);

  useEffect(() => {
    localStorage.setItem('exchangeRate', exchangeRate.toString());
  }, [exchangeRate]);

  // Fetch current exchange rate from API
const fetchCurrentExchangeRate = async () => {
  try {
    setLoading(true);
    const response = await fetch('https://api.exchangerate-api.com/v4/latest/GBP');
    const data = await response.json();
    const currentRate = data.rates.PKR;
    setExchangeRate(currentRate);
    return currentRate;
  } catch (error) {
    console.error('Error:', error);
    setError('Failed to fetch rate');
    return exchangeRate;
  } finally {
    setLoading(false);
  }
};

  // Update currency settings
  const updateCurrencySettings = async (newCurrency, newExchangeRate = exchangeRate) => {
    try {
      setLoading(true);
      setError(null);

      const rate = parseFloat(newExchangeRate);
      if (isNaN(rate) || rate <= 0 || rate > 500) {
        throw new Error('Exchange rate must be a number between 1 and 500');
      }

      setCurrency(newCurrency);
      setExchangeRate(rate);

      return { success: true, data: { base_currency: newCurrency, exchange_rate: rate } };
    } catch (error) {
      console.error('Error updating currency settings:', error);
      setError(error.message);
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  // FIXED: Convert PKR amount to selected currency
  const convertPrice = (priceInPKR) => {
    const price = parseFloat(priceInPKR) || 0;
    if (currency === 'GBP') {
      // Convert PKR to GBP: divide by exchange rate
      return price / exchangeRate;
    } else {
      // Already in PKR, return as is
      return price;
    }
  };

  // FIXED: Format price based on current currency
  const formatPrice = (priceInPKR) => {
    const price = parseFloat(priceInPKR) || 0;
    if (currency === 'GBP') {
      // Convert PKR to GBP and format
      const priceInGBP = price / exchangeRate;
      return `£${priceInGBP.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    } else {
      // Already in PKR, format as PKR
      return `Rs. ${price.toLocaleString('en-PK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }
  };

  // Format price without symbol
  const formatPriceNumber = (priceInPKR) => {
    const price = parseFloat(priceInPKR) || 0;
    if (currency === 'GBP') {
      const priceInGBP = price / exchangeRate;
      return priceInGBP.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    } else {
      return price.toLocaleString('en-PK', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }
  };

  // Get current currency symbol
  const getCurrencySymbol = () => {
    return currency === 'GBP' ? '£' : 'Rs.';
  };

  // FIXED: Convert from current currency back to PKR
  const convertToBase = (priceInCurrentCurrency) => {
    const price = parseFloat(priceInCurrentCurrency) || 0;
    if (currency === 'GBP') {
      // Convert GBP to PKR: multiply by exchange rate
      return price * exchangeRate;
    } else {
      // Already in PKR, return as is
      return price;
    }
  };

  // Convert GBP to PKR (if you have GBP input)
  const convertGBPtoPKR = (priceInGBP) => {
    const price = parseFloat(priceInGBP) || 0;
    return price * exchangeRate;
  };

  // Get raw exchange rate
  const getExchangeRate = () => {
    return exchangeRate;
  };

  // Get current currency code
  const getCurrentCurrency = () => {
    return currency;
  };

  // Clear error
  const clearError = () => {
    setError(null);
  };

  // Refresh settings
  const refreshSettings = async () => {
    try {
      const currentRate = await fetchCurrentExchangeRate();
      return {
        success: true,
        data: {
          base_currency: currency,
          exchange_rate: currentRate
        }
      };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const value = {
    currency,
    exchangeRate,
    loading,
    error,
    formatPrice,
    formatPriceNumber,
    getCurrencySymbol,
    convertPrice,           // PKR → Selected currency
    convertToBase,          // Selected currency → PKR
    convertGBPtoPKR,        // GBP → PKR
    getExchangeRate,
    getCurrentCurrency,
    updateCurrencySettings,
    refreshSettings,
    clearError,
    fetchCurrentExchangeRate
  };

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
};